import { TooltipHost, Icon } from "@fluentui/react";
import React, { useContext } from "react";
import {
    createListTableColumns,
    renderName,
    renderDateTime,
    ensure,
    Badge,
    BadgeColor,
    ListTableColumn,
    Text,
    TableColumn,
    renderOptionalDateTime,
    renderDate,
    Stack
} from "sprint-common-web";

import { renderContent } from "../../common/components/listings/renderContent";
import { renderOrderProgress } from "../../common/components/listings/renderOrderProgress";
import { renderOrderResolutions } from "../../common/components/listings/renderOrderResolutions";
import { renderRuntime } from "../../common/components/listings/renderRuntime";
import { renderSoundtracksAvailabilityList } from "../../common/components/listings/renderSoundtracksAvailability";
import { renderSubtitlesAvailabilityList } from "../../common/components/listings/renderSubtitlesAvailabilityList";
import { renderTargetFilenames } from "../../common/components/listings/renderTargetFilenames";
import { renderVideoAvailability } from "../../common/components/listings/renderVideoAvailability";
import { getOrderDashboardHref } from "../../OrdersRoutes";
import { getStatusName } from "../common/utils/getStatusName";
import { resolveCloudOrderSourceBadgeColor } from "../common/utils/resolveCloudOrderSourceBadgeColor";
import { OrderActionsListingButton } from "./actions/OrderActionsListingButtons";
import { PinOrderListingButton } from "./actions/PinOrderListingButton";
import { getSortedOrderColumns, OrderColumn, OrderListingConfig } from "./config/OrderListingConfig";
import { OrderListingContext } from "./context/OrderListingContext";
import { CloudOrderSource, OrderListingItem, isDeliveryFolderMissingWhenRequired } from "./OrderListingItem";

type OrderItem = OrderListingItem & { info: string; resolution: string };

type OrderListingColumn = TableColumn<OrderItem>;

export const createOrderListingColumns = (): ListTableColumn<OrderListingItem>[] => {
    const { listingConfig } = useContext(OrderListingContext);

    const columns = getListingColumns(listingConfig);

    return createListTableColumns<OrderItem>(columns);
};

const getListingColumns = (config: OrderListingConfig): OrderListingColumn[] => {
    const columnMapping: Record<OrderColumn, OrderListingColumn> = {
        [OrderColumn.PoNumber]: {
            type: "link",
            key: "pONumber",
            name: "PO Number",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            getHref: (item: OrderListingItem) => getOrderDashboardHref(item.id)
        },
        [OrderColumn.Airline]: {
            type: "basic",
            key: "airline",
            name: "Airline",
            minWidth: 100,
            maxWidth: 180,
            isResizable: true,
            isSortable: true,
            onRender: renderName
        },
        [OrderColumn.System]: {
            type: "basic",
            key: "system",
            name: "System",
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderName
        },
        [OrderColumn.StartDate]: {
            type: "basic",
            key: "startDate",
            name: "Start Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },
        [OrderColumn.DueDate]: {
            type: "basic",
            key: "dueDate",
            name: "Due Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },
        [OrderColumn.DeliveryDate]: {
            type: "basic",
            key: "deliveryDate",
            name: "Delivery Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderOptionalDateTime
        },
        [OrderColumn.Progress]: {
            type: "basic",
            key: "progress",
            name: "Progress",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderOrderProgress
        },
        [OrderColumn.Info]: {
            type: "basic",
            key: "info",
            name: "Info",
            minWidth: 80,
            maxWidth: 130,
            isResizable: true,
            onRender: renderInfo
        },
        [OrderColumn.TargetFilenames]: {
            type: "basic",
            key: "targetFilenames",
            name: "Target filenames",
            minWidth: 120,
            maxWidth: 200,
            isResizable: true,
            onRender: renderTargetFilenames
        },
        [OrderColumn.Resolution]: {
            type: "basic",
            key: "resolution",
            name: "Resolution",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item) =>
                renderOrderResolutions({
                    targetResolution: item?.video.targetResolution,
                    sourceResolution: item?.video.sourceResolution
                })
        },
        [OrderColumn.Video]: {
            type: "basic",
            key: "video",
            name: "Video",
            minWidth: 40,
            maxWidth: 40,
            isResizable: true,
            onRender: renderVideoAvailability
        },
        [OrderColumn.Runtime]: {
            type: "basic",
            key: "runtime",
            name: "Runtime",
            minWidth: 60,
            maxWidth: 80,
            onRender: renderRuntime
        },
        [OrderColumn.Soundtracks]: {
            type: "basic",
            key: "soundtracks",
            name: "Soundtracks",
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderSoundtracksAvailabilityList
        },
        [OrderColumn.Subtitles]: {
            type: "basic",
            key: "subtitles",
            name: "Subtitles",
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderSubtitlesAvailabilityList
        },
        [OrderColumn.Status]: {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: renderOrderStatusWithInfo
        },
        [OrderColumn.CreatedDate]: {
            type: "basic",
            key: "createdDate",
            name: "Created",
            minWidth: 150,
            maxWidth: 150,
            isSortable: true,
            isResizable: true,
            onRender: renderDateTime
        },
        [OrderColumn.Content]: {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 100,
            maxWidth: 400,
            isResizable: true,
            isSortable: true,
            onRender: renderContent
        },
        [OrderColumn.Source]: {
            type: "basic",
            key: "source",
            name: "Source",
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: renderSource
        },
        [OrderColumn.Actions]: {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            buttons: [
                {
                    id: "actions",
                    render: OrderActionsListingButton,
                    testId: "order-actions-button"
                },
                {
                    id: "pin",
                    render: PinOrderListingButton
                }
            ]
        }
    };

    const columns: OrderListingColumn[] = [];

    getSortedOrderColumns(config.columns).forEach(([key, column]) => {
        const resolvedKey = Number(key) as OrderColumn;

        if (column.visible) {
            columns.push(columnMapping[resolvedKey]);
        }
    });

    return columns;
};
const renderOrderStatusWithInfo = (item?: OrderListingItem) => {
    ensure(item);
    const status = getStatusName(item.status) + " ";

    return (
        <Stack data-testId="order-status" styles={{ root: { display: "flex", flexDirection: "row" } }}>
            {status}
            {item.statusDetails && (
                <TooltipHost content={item.statusDetails}>
                    <Icon iconName="Info" styles={{ root: { marginLeft: 5 } }} />
                </TooltipHost>
            )}
        </Stack>
    );
};

const renderInfo = (item?: OrderListingItem) => {
    ensure(item);

    const isDeliveryFolderMissing = isDeliveryFolderMissingWhenRequired(item);

    if (!isDeliveryFolderMissing && !item.isWatermark && !item.isRevised && !item.isTrailer) {
        return <Text value="-" />;
    }

    return (
        <Stack style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
            {isDeliveryFolderMissingWhenRequired(item) && (
                <Badge color={BadgeColor.Gray} text="Missing delivery folder" />
            )}
            {!!item.isTrailer && <Badge color={BadgeColor.ApplicationPrimary} text="Trailer" />}
            {!!item.isRevised && <Badge color={BadgeColor.LightBlue} text="Revised" />}
            {!!item.isWatermark && <Badge color={BadgeColor.Green} text="Watermark" />}
        </Stack>
    );
};

const renderSource = (item?: OrderListingItem) => {
    ensure(item);

    const badgeColor = resolveCloudOrderSourceBadgeColor(item.source);

    return <Badge color={badgeColor} text={CloudOrderSource[item.source]} />;
};
